import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Badge,
  Carousel,
  Alert,
  Spinner,
  Modal
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faHeart,
  faEye,
  faComments,
  faHome,
  faMapMarkerAlt,
  faBed,
  faBath,
  faRuler,
  faGraduationCap,
  faRobot,
  faCog,
  faSearch,
  faCalendarCheck
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './CustomerPortalChat.css';

interface Message {
  id: string;
  sender: 'ai' | 'user';
  content: string;
  timestamp: Date;
  properties?: Property[];
  type?: 'text' | 'property-cards' | 'quick-actions';
}

interface Property {
  mlsNumber: string;
  price: number;
  address: {
    street: string;
    city: string;
    state: string;
  };
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  images?: string[];
  schoolRating?: number;
  whyRecommended?: string[];
}

interface Preferences {
  areas: string[];
  budgetMin: number;
  budgetMax: number;
  mustHaves: string[];
  bedrooms: number;
  bathrooms: number;
}

interface CustomerPortalChatProps {
  user?: any;
  isAuthenticated?: boolean;
}

const CustomerPortalChat: React.FC<CustomerPortalChatProps> = ({ user, isAuthenticated }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      sender: 'ai',
      content: "Hi! I'm Theta, your personal AI home search assistant. I'm here to help build your perfect home search profile. Tell me, what's most important to you in your new home? (location, price range, features, lifestyle needs)",
      timestamp: new Date(),
      type: 'text'
    }
  ]);
  const [buyerProfileId, setBuyerProfileId] = useState<string | null>(null);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [workflowSessionId, setWorkflowSessionId] = useState<string | null>(null);
  const [isPremium, setIsPremium] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [preferences, setPreferences] = useState<Preferences>({
    areas: [],
    budgetMin: 0,
    budgetMax: 0,
    mustHaves: [],
    bedrooms: 0,
    bathrooms: 0
  });
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize buyer profile session when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user && !workflowSessionId) {
      initializeBuyerProfile();
    }
  }, [isAuthenticated, user]);

  const initializeBuyerProfile = async () => {
    try {
      // First check if user already has a lead/buyer profile
      const response = await axiosInstance.get('/buyer-profiles');
      
      if (response.data.data.buyer_profiles.length > 0) {
        // Use existing profile
        const profile = response.data.data.buyer_profiles[0];
        setBuyerProfileId(profile.id);
        setLeadId(profile.lead_id);
      } else {
        // Create a new lead and buyer profile for this user
        // This would typically be done through the lead creation flow
        toast.success('Welcome! Let\'s build your home search profile.');
      }
    } catch (error) {
      console.error('Failed to initialize buyer profile:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Check for premium content requests
    const lowerInput = inputMessage.toLowerCase();
    const isPremiumRequest = 
      lowerInput.includes('coffee shop') ||
      lowerInput.includes('restaurant') ||
      lowerInput.includes('walkability') ||
      lowerInput.includes('school rating') ||
      lowerInput.includes('safety') ||
      lowerInput.includes('commute') ||
      lowerInput.includes('farmers market') ||
      lowerInput.includes('dog park') ||
      lowerInput.includes('neighborhood vibe') ||
      lowerInput.includes('value forecast') ||
      lowerInput.includes('renovation');

    if (isPremiumRequest && !isPremium) {
      setShowPremiumModal(true);
      return;
    }

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      sender: 'user',
      content: inputMessage,
      timestamp: new Date(),
      type: 'text'
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Check if user needs to sign in to save profile
      if (!isAuthenticated && messages.length > 3) {
        setTimeout(() => {
          const aiResponse: Message = {
            id: Date.now().toString() + '-ai',
            sender: 'ai',
            content: "I'm learning a lot about what you're looking for! To save your search profile and get personalized property recommendations, please sign in or create a free account. Would you like me to help you with that?",
            timestamp: new Date(),
            type: 'text'
          };
          setMessages(prev => [...prev, aiResponse]);
          setIsTyping(false);
        }, 1000);
        return;
      }

      // Send to AI agent - use workflow endpoint if authenticated
      let response: any;
      if (workflowSessionId) {
        response = await axiosInstance.post(`/buyer-profiles/workflow-session/${workflowSessionId}/chat`, {
          message: inputMessage,
          leadId: leadId
        });
      } else {
        // Use general chat endpoint for unauthenticated users
        response = await axiosInstance.post('/theta-chat/chat', {
          message: inputMessage,
          conversationHistory: messages.slice(-5), // Last 5 messages for context
          preferences: preferences,
          profileBuildingMode: true // Indicates we're only building search profile
        });
      }

      // Handle AI response
      setTimeout(() => {
        const aiResponse: Message = {
          id: Date.now().toString() + '-ai',
          sender: 'ai',
          content: response.data.message || response.data.response,
          timestamp: new Date(),
          type: response.data.type || 'text',
          properties: response.data.properties
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);

        // Update preferences if AI learned something new
        if (response.data.updatedPreferences) {
          setPreferences(response.data.updatedPreferences);
        }
      }, 1000);
    } catch (error) {
      console.error('Error sending message:', error);
      setIsTyping(false);
      
      // Fallback response
      const fallbackResponse: Message = {
        id: Date.now().toString() + '-ai',
        sender: 'ai',
        content: isPremium ? 
          "I'm focused on understanding your home search needs. Could you tell me more about your preferred location, budget, or must-have features?" :
          "ERROR: Please provide: CITY, PRICE RANGE, BEDROOMS, or TIMELINE.",
        timestamp: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, fallbackResponse]);
    }
  };

  const renderMessage = (message: Message) => {
    if (message.type === 'property-cards' && message.properties) {
      return (
        <div className="property-cards-container">
          <p className="mb-3">{message.content}</p>
          {message.properties.map((property, index) => (
            <PropertyCard key={property.mlsNumber} property={property} />
          ))}
        </div>
      );
    }

    return <p className="mb-0">{message.content}</p>;
  };

  const PropertyCard: React.FC<{ property: Property }> = ({ property }) => (
    <Card className="property-card mb-3">
      <Card.Body>
        <Row>
          <Col md={4}>
            {property.images && property.images.length > 0 ? (
              <Carousel controls={false} indicators={false}>
                {property.images.slice(0, 3).map((image, idx) => (
                  <Carousel.Item key={idx}>
                    <img 
                      src={image} 
                      alt={`Property ${idx + 1}`}
                      className="property-image"
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              <div className="property-image-placeholder">
                <FontAwesomeIcon icon={faHome} size="3x" />
              </div>
            )}
          </Col>
          <Col md={8}>
            <div className="property-details">
              <h5 className="property-price">${property.price.toLocaleString()}</h5>
              <p className="property-specs">
                {property.bedrooms} bed | {property.bathrooms} bath | {property.sqft.toLocaleString()} sqft
              </p>
              <p className="property-address">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                {property.address.street}, {property.address.city}
              </p>
              {property.schoolRating && (
                <p className="school-rating">
                  <FontAwesomeIcon icon={faGraduationCap} className="me-2" />
                  Excellent Schools ({property.schoolRating}/10 rating)
                </p>
              )}
              {property.whyRecommended && (
                <div className="why-recommended">
                  <strong>✨ Why I picked this for you:</strong>
                  <ul className="mb-0">
                    {property.whyRecommended.map((reason, idx) => (
                      <li key={idx}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="property-actions mt-3">
                <Button size="sm" variant="outline-primary" className="me-2">
                  <FontAwesomeIcon icon={faComments} className="me-1" />
                  Ask about this
                </Button>
                <Button size="sm" variant="outline-primary" className="me-2">
                  <FontAwesomeIcon icon={faHeart} className="me-1" />
                  Save
                </Button>
                <Button size="sm" variant="primary">
                  <FontAwesomeIcon icon={faEye} className="me-1" />
                  Details
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  return (
    <Container fluid className="customer-portal-chat">
      <Row className="h-100">
        {/* Main Chat Area */}
        <Col md={showSidebar ? 8 : 12} className={`chat-area ${!isPremium ? 'free-tier' : 'premium'}`}>
          <div className="chat-header">
            <div style={{ flex: 1 }}>
              <h4 className="mb-0">
                <FontAwesomeIcon icon={faRobot} className="me-2" />
                {isPremium ? 'Theta AI Premium | Community Intelligence' : 'Theta Profile Builder [FREE TIER]'}
              </h4>
              {!isPremium && (
                <div style={{ marginTop: '8px' }}>
                  <Badge 
                    bg="warning" 
                    className="upgrade-badge"
                    style={{ 
                      cursor: 'pointer', 
                      fontSize: '0.9rem',
                      padding: '6px 12px',
                      display: 'inline-block'
                    }} 
                    onClick={() => setShowPremiumModal(true)}
                  >
                    🔓 Upgrade for Conversational AI - $10/mo
                  </Badge>
                </div>
              )}
            </div>
            <Button
              variant="link"
              onClick={() => setShowSidebar(!showSidebar)}
            >
              <FontAwesomeIcon icon={faCog} />
            </Button>
          </div>

          <div className="messages-container">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender === 'ai' ? 'ai-message' : 'user-message'}`}
              >
                <div className="message-header">
                  {message.sender === 'ai' ? '🤖 Theta' : '👤 You'}
                  <span className="message-time">
                    {message.timestamp.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
                <div className="message-content">
                  {renderMessage(message)}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="message ai-message">
                <div className="message-header">🤖 Theta</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-area">
            <Form onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Type your message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  className="chat-input"
                />
              </Form.Group>
              <div className="quick-actions">
                <Button size="sm" variant="outline-secondary" className="me-2">
                  <FontAwesomeIcon icon={faHome} className="me-1" />
                  Show saved
                </Button>
                <Button size="sm" variant="outline-secondary" className="me-2">
                  <FontAwesomeIcon icon={faSearch} className="me-1" />
                  New search
                </Button>
                <Button size="sm" variant="outline-secondary">
                  <FontAwesomeIcon icon={faCalendarCheck} className="me-1" />
                  Schedule tour
                </Button>
              </div>
            </Form>
          </div>
        </Col>

        {/* Preferences Sidebar */}
        {showSidebar && (
          <Col md={4} className="preferences-sidebar">
            <Card className="h-100">
              <Card.Body>
                <h5>Your Preferences</h5>
                
                <div className="preference-section">
                  <h6>📍 Areas:</h6>
                  {preferences.areas.length > 0 ? (
                    preferences.areas.map((area, idx) => (
                      <Badge key={idx} bg="primary" className="me-2 mb-2">
                        {area} ✓
                      </Badge>
                    ))
                  ) : (
                    <p className="text-muted">No areas selected yet</p>
                  )}
                  <Button size="sm" variant="link">+ Add area</Button>
                </div>

                <div className="preference-section">
                  <h6>💰 Budget:</h6>
                  {preferences.budgetMax > 0 ? (
                    <p>${preferences.budgetMin.toLocaleString()} - ${preferences.budgetMax.toLocaleString()}</p>
                  ) : (
                    <p className="text-muted">Not set</p>
                  )}
                  <Button size="sm" variant="link">Adjust</Button>
                </div>

                <div className="preference-section">
                  <h6>🏠 Must-haves:</h6>
                  {preferences.mustHaves.length > 0 ? (
                    preferences.mustHaves.map((feature, idx) => (
                      <Badge key={idx} bg="secondary" className="me-2 mb-2">
                        {feature} ✓
                      </Badge>
                    ))
                  ) : (
                    <p className="text-muted">Tell me what you need!</p>
                  )}
                </div>

                <hr />

                <Alert variant="info">
                  <h6>💡 Theta Suggests:</h6>
                  <p className="mb-2">
                    "Based on your interests, you might also like properties in Tempe near ASU"
                  </p>
                  <Button size="sm" variant="info">Explore Tempe</Button>
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Premium Features Modal */}
      <Modal 
        show={showPremiumModal} 
        onHide={() => setShowPremiumModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faHome} className="me-2" />
            Unlock Chat Premium - Community Intelligence
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <h3 className="text-primary">$10/month</h3>
            <p className="lead">Transform your home search with AI-powered community insights</p>
          </div>
          
          <Row>
            <Col md={6}>
              <h5>🏘️ Neighborhood Intelligence</h5>
              <ul>
                <li>Best local coffee shops & cafes</li>
                <li>Family-owned restaurants</li>
                <li>Neighborhood pubs & hangouts</li>
                <li>Hidden gems via Yelp integration</li>
                <li>Weekend farmers markets</li>
                <li>Dog parks & pet-friendly spots</li>
              </ul>
            </Col>
            <Col md={6}>
              <h5>📊 Advanced Analysis</h5>
              <ul>
                <li>Detailed walkability scores</li>
                <li>School ratings & reviews</li>
                <li>Safety & crime statistics</li>
                <li>Commute time analysis</li>
                <li>Property value forecasts</li>
                <li>Renovation ROI calculator</li>
              </ul>
            </Col>
          </Row>
          
          <Alert variant="info" className="mt-3">
            <strong>🤝 Supporting Local Communities</strong><br/>
            Your subscription powers our lifestyle intelligence system and helps fund local housing nonprofits.
          </Alert>
          
          <div className="text-center mt-4">
            <p className="text-muted mb-3">
              <small>Currently in free tier: Building your search profile only</small>
            </p>
            {isAuthenticated ? (
              <Button variant="primary" size="lg" onClick={() => {
                // TODO: Implement subscription flow
                toast.success('Premium subscription coming soon!');
                setShowPremiumModal(false);
              }}>
                Upgrade to Premium - $10/mo
              </Button>
            ) : (
              <div>
                <Button variant="primary" size="lg" onClick={() => navigate('/sign-up')}>
                  Sign Up & Get Premium
                </Button>
                <p className="mt-2">
                  <Button variant="link" onClick={() => navigate('/sign-in')}>
                    Already have an account? Sign in
                  </Button>
                </p>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default CustomerPortalChat;